/**
 * Notifications Module
 *
 * Toast notification system for displaying success, error, warning, and info messages.
 * Automatically integrates with the global window.showFlashMessage if available.
 */

import { ready } from '../utils/dom.js';

// Notification container ID
const CONTAINER_ID = 'notification-container';

// Default notification settings
const DEFAULTS = {
    duration: 5000,
    position: 'top-right',
    closable: true
};

// CSS classes for different notification types
const TYPE_CLASSES = {
    success: 'bg-green-50 border-green-200 text-green-800',
    error: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800'
};

// Icons for different notification types
const TYPE_ICONS = {
    success: `<svg class="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
    </svg>`,
    error: `<svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
    </svg>`,
    warning: `<svg class="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
    </svg>`,
    info: `<svg class="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
    </svg>`
};

/**
 * Get or create the notification container
 * @returns {Element} The notification container
 */
function getContainer() {
    let container = document.getElementById(CONTAINER_ID);

    if (!container) {
        container = document.createElement('div');
        container.id = CONTAINER_ID;
        container.className = 'fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-sm';
        container.setAttribute('aria-live', 'polite');
        container.setAttribute('aria-atomic', 'true');
        document.body.appendChild(container);
    }

    return container;
}

/**
 * Create a notification element
 * @param {string} message - The notification message
 * @param {string} type - Notification type (success, error, warning, info)
 * @param {Object} options - Additional options
 * @returns {Element} The notification element
 */
function createNotification(message, type, options) {
    const notification = document.createElement('div');
    notification.className = `flex items-start gap-3 p-4 border rounded-lg shadow-lg transform transition-all duration-300 ease-out translate-x-full opacity-0 ${TYPE_CLASSES[type] || TYPE_CLASSES.info}`;
    notification.setAttribute('role', 'alert');

    const icon = TYPE_ICONS[type] || TYPE_ICONS.info;

    notification.innerHTML = `
        <div class="flex-shrink-0">
            ${icon}
        </div>
        <div class="flex-1 text-sm font-medium">
            ${escapeHtml(message)}
        </div>
        ${options.closable ? `
        <button type="button" class="flex-shrink-0 ml-2 text-gray-400 hover:text-gray-600 focus:outline-none" aria-label="Close">
            <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
        </button>
        ` : ''}
    `;

    return notification;
}

/**
 * Escape HTML to prevent XSS
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
}

/**
 * Show a notification
 *
 * @param {string} message - The notification message
 * @param {string} type - Notification type: 'success', 'error', 'warning', 'info'
 * @param {Object} options - Additional options
 * @param {number} options.duration - Duration in ms (0 for persistent)
 * @param {boolean} options.closable - Whether to show close button
 * @returns {Element} The notification element
 */
export function show(message, type = 'info', options = {}) {
    const opts = { ...DEFAULTS, ...options };
    const container = getContainer();
    const notification = createNotification(message, type, opts);

    container.appendChild(notification);

    // Trigger animation
    requestAnimationFrame(() => {
        notification.classList.remove('translate-x-full', 'opacity-0');
    });

    // Add close button handler
    const closeBtn = notification.querySelector('button');
    if (closeBtn) {
        closeBtn.addEventListener('click', () => dismiss(notification));
    }

    // Auto-dismiss after duration
    if (opts.duration > 0) {
        setTimeout(() => dismiss(notification), opts.duration);
    }

    return notification;
}

/**
 * Dismiss a notification
 * @param {Element} notification - The notification element to dismiss
 */
export function dismiss(notification) {
    if (!notification || !notification.parentNode) return;

    notification.classList.add('translate-x-full', 'opacity-0');

    setTimeout(() => {
        if (notification.parentNode) {
            notification.parentNode.removeChild(notification);
        }
    }, 300);
}

/**
 * Clear all notifications
 */
export function clearAll() {
    const container = document.getElementById(CONTAINER_ID);
    if (container) {
        const notifications = container.querySelectorAll('[role="alert"]');
        notifications.forEach(n => dismiss(n));
    }
}

// Convenience methods
export const success = (message, options) => show(message, 'success', options);
export const error = (message, options) => show(message, 'error', options);
export const warning = (message, options) => show(message, 'warning', options);
export const info = (message, options) => show(message, 'info', options);

/**
 * Legacy compatibility function
 * Maps to window.showFlashMessage pattern used in existing code
 */
export function showNotification(message, type = 'info') {
    // Map legacy type names
    const typeMap = {
        'success': 'success',
        'error': 'error',
        'danger': 'error',
        'warning': 'warning',
        'info': 'info'
    };

    return show(message, typeMap[type] || 'info');
}

/**
 * Handle server-rendered flash messages
 * Automatically dismisses static flash messages after a delay
 */
function handleServerFlashMessages() {
    const container = document.getElementById('server-flash-messages');
    if (!container) return;

    const messages = container.querySelectorAll('div');
    if (messages.length === 0) return;

    messages.forEach(message => {
        // Add transition classes for smooth exit
        message.classList.add('transform', 'transition-all', 'duration-500', 'ease-out');

        // Auto-dismiss after 5 seconds
        setTimeout(() => {
            // Add classes to trigger fade out
            message.classList.add('opacity-0');

            // Remove from DOM after animation
            setTimeout(() => {
                if (message.parentNode) {
                    message.parentNode.removeChild(message);
                }

                // If container is empty, remove it too to fix spacing
                if (container.children.length === 0) {
                    container.style.display = 'none';
                }
            }, 500);
        }, 5000);
    });
}

// Initialize and expose globally
ready(() => {
    // Create container early
    getContainer();

    // Handle server-side flash messages
    handleServerFlashMessages();

    // Expose globally for compatibility with existing code
    window.showNotification = showNotification;
    window.showFlashMessage = showNotification;

    // Also expose the module functions
    window.notifications = {
        show,
        dismiss,
        clearAll,
        success,
        error,
        warning,
        info
    };
});

export default {
    show,
    dismiss,
    clearAll,
    success,
    error,
    warning,
    info,
    showNotification
};
